import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { poAPI } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import {
  Search, SlidersHorizontal, X, FileText, ChevronRight, Plus,
  Package, Truck, CheckCircle2, Clock, AlertTriangle, Award,
} from 'lucide-react';
import VendorScorecard, { VendorScoreWarning } from '@/components/VendorScorecard';

const STATUS_COLORS = {
  draft: { bg: 'rgba(107,114,128,0.10)', text: '#6B7280', border: 'rgba(107,114,128,0.25)' },
  sent: { bg: 'rgba(37,99,235,0.08)', text: '#2563EB', border: 'rgba(37,99,235,0.25)' },
  acknowledged: { bg: 'rgba(6,182,212,0.08)', text: '#0891B2', border: 'rgba(6,182,212,0.25)' },
  partially_received: { bg: 'rgba(245,158,11,0.10)', text: '#D97706', border: 'rgba(245,158,11,0.25)' },
  fully_received: { bg: 'rgba(16,185,129,0.10)', text: '#059669', border: 'rgba(16,185,129,0.30)' },
  cancelled: { bg: 'rgba(239,68,68,0.08)', text: '#DC2626', border: 'rgba(239,68,68,0.25)' },
};
const STATUS_LABELS = {
  draft: 'Draft', sent: 'Sent', acknowledged: 'Acknowledged',
  partially_received: 'Partial', fully_received: 'Received', cancelled: 'Cancelled',
};

function StatusBadge({ status }) {
  const c = STATUS_COLORS[status] || STATUS_COLORS.draft;
  return (
    <Badge variant="outline" className="text-[10px] font-semibold px-2 py-0.5 rounded-md whitespace-nowrap"
      style={{ backgroundColor: c.bg, color: c.text, borderColor: c.border }}>
      {STATUS_LABELS[status] || status}
    </Badge>
  );
}

export default function PurchaseOrdersPage() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('pos');
  const [pos, setPos] = useState([]);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pendingLoading, setPendingLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedItems, setSelectedItems] = useState({});
  const [generating, setGenerating] = useState({});

  const fetchPOs = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      const res = await poAPI.list(params);
      setPos(res.data);
    } catch (err) {
      console.error('Failed to fetch POs', err);
    }
    setLoading(false);
  }, [search, statusFilter]);

  const fetchPending = async () => {
    setPendingLoading(true);
    try {
      const res = await poAPI.pending();
      setPending(res.data);
    } catch (err) {
      console.error('Failed to fetch pending', err);
    }
    setPendingLoading(false);
  };

  useEffect(() => { fetchPOs(); fetchPending(); }, []);

  const toggleItem = (itemId, vendorName) => {
    setSelectedItems(prev => {
      const key = vendorName;
      const current = prev[key] || new Set();
      const next = new Set(current);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return { ...prev, [key]: next };
    });
  };

  const selectAll = (vendorName, items) => {
    const ids = items.map(i => i.id);
    setSelectedItems(prev => ({ ...prev, [vendorName]: new Set(ids) }));
  };

  const generatePO = async (vendorName) => {
    const ids = Array.from(selectedItems[vendorName] || []);
    if (!ids.length) { toast.error('Select at least one item'); return; }
    setGenerating(p => ({ ...p, [vendorName]: true }));
    try {
      const res = await poAPI.create({ vendor_name: vendorName, line_item_ids: ids });
      toast.success(`${res.data.po_number} created`);
      setSelectedItems(prev => ({ ...prev, [vendorName]: new Set() }));
      fetchPending();
      fetchPOs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate PO');
    }
    setGenerating(p => ({ ...p, [vendorName]: false }));
  };

  const handleSearch = (e) => { e.preventDefault(); fetchPOs(); };
  const hasFilters = search || statusFilter;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl sm:text-4xl font-semibold" style={{ color: 'var(--brand-navy)' }}>Purchase Orders</h1>
        <p className="text-sm mt-1" style={{ color: 'var(--ink-muted)' }}>Fabric procurement & vendor POs</p>
      </div>

      {/* Tab Navigation */}
      <div className="flex items-center gap-1 border-b" style={{ borderColor: 'rgba(27,42,74,0.08)' }}>
        <button onClick={() => setActiveTab('pos')}
          className={`px-4 py-2.5 text-xs font-semibold border-b-2 transition-colors ${activeTab === 'pos' ? 'border-[color:var(--brand-navy)]' : 'border-transparent hover:bg-gray-50'}`}
          style={{ color: activeTab === 'pos' ? 'var(--brand-navy)' : 'var(--ink-muted)' }}
          data-testid="po-tab-pos">
          <Truck className="w-3.5 h-3.5 inline mr-1.5" />Purchase Orders
        </button>
        <button onClick={() => setActiveTab('scorecard')}
          className={`px-4 py-2.5 text-xs font-semibold border-b-2 transition-colors ${activeTab === 'scorecard' ? 'border-[color:var(--brand-navy)]' : 'border-transparent hover:bg-gray-50'}`}
          style={{ color: activeTab === 'scorecard' ? 'var(--brand-navy)' : 'var(--ink-muted)' }}
          data-testid="po-tab-scorecard">
          <Award className="w-3.5 h-3.5 inline mr-1.5" />Vendor Scorecard
        </button>
      </div>

      {/* Vendor Scorecard Tab */}
      {activeTab === 'scorecard' && <VendorScorecard />}

      {/* POs Tab */}
      {activeTab === 'pos' && <>

      {/* Generate Daily POs */}
      {!pendingLoading && pending.length > 0 && (
        <Card className="rounded-xl border-2 bg-white shadow-sm" style={{ borderColor: 'rgba(197,165,90,0.4)' }} data-testid="pending-po-section">
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(197,165,90,0.12)' }}>
                <Truck className="w-4 h-4" style={{ color: 'var(--brand-gold)' }} />
              </div>
              <div>
                <CardTitle className="text-base" style={{ color: 'var(--brand-navy)' }}>Generate Daily POs</CardTitle>
                <p className="text-xs" style={{ color: 'var(--ink-muted)' }}>
                  {pending.reduce((s, v) => s + v.items.length, 0)} items from confirmed orders awaiting PO generation
                </p>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {pending.map(vendor => {
              const sel = selectedItems[vendor.vendor_name] || new Set();
              const allSelected = sel.size === vendor.items.length && vendor.items.length > 0;
              return (
                <div key={vendor.vendor_name} className="rounded-lg border p-3" style={{ borderColor: 'rgba(27,42,74,0.08)' }}
                  data-testid={`pending-vendor-${vendor.vendor_name}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Checkbox checked={allSelected} onCheckedChange={() => {
                        if (allSelected) setSelectedItems(p => ({ ...p, [vendor.vendor_name]: new Set() }));
                        else selectAll(vendor.vendor_name, vendor.items);
                      }} data-testid={`select-all-${vendor.vendor_name}`} />
                      <p className="text-sm font-semibold" style={{ color: 'var(--brand-navy)' }}>{vendor.vendor_name}</p>
                      <Badge variant="outline" className="text-[9px] px-1.5 rounded-md" style={{ color: 'var(--ink-muted)' }}>
                        {vendor.items.length} item{vendor.items.length > 1 ? 's' : ''} &middot; {vendor.total_metres}m &middot; &#8377;{vendor.total_amount.toLocaleString('en-IN')}
                      </Badge>
                    </div>
                    <Button size="sm" className="gap-1 rounded-lg text-xs h-8"
                      style={{ backgroundColor: 'var(--brand-navy)' }}
                      disabled={sel.size === 0 || generating[vendor.vendor_name]}
                      onClick={() => generatePO(vendor.vendor_name)}
                      data-testid={`generate-po-${vendor.vendor_name}`}>
                      <FileText className="w-3 h-3" />
                      {generating[vendor.vendor_name] ? 'Creating...' : 'Generate PO'}
                    </Button>
                  </div>
                  <div className="space-y-1">
                    {vendor.items.map(item => (
                      <div key={item.id} className="flex items-center gap-2 py-1 px-1 rounded hover:bg-[color:var(--surface-2)]"
                        style={{ transition: 'background 120ms' }}>
                        <Checkbox checked={sel.has(item.id)}
                          onCheckedChange={() => toggleItem(item.id, vendor.vendor_name)}
                          data-testid={`pending-item-${item.id}`} />
                        <div className="flex-1 flex items-center justify-between">
                          <div>
                            <span className="text-xs font-medium" style={{ color: 'var(--navy-ink)' }}>
                              {item.fabric_code_vendor || item.fabric_code_internal}
                            </span>
                            <span className="text-[10px] ml-2" style={{ color: 'var(--ink-muted)' }}>
                              {item.quantity_metres}m &middot; {item.client_initials}/{item.order_number} &middot; {item.garment_type}
                            </span>
                          </div>
                          <span className="text-xs font-medium tabular-nums" style={{ color: 'var(--navy-ink)' }}>
                            &#8377;{item.amount.toLocaleString('en-IN')}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* Search & Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <form onSubmit={handleSearch} className="flex-1 min-w-[200px] max-w-md relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--ink-muted)' }} />
          <Input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search PO# or vendor..." className="pl-9 h-10 rounded-lg" data-testid="po-search-input" />
        </form>
        <Button variant="outline" size="sm" className="gap-1.5 h-10 rounded-lg"
          onClick={() => setShowFilters(!showFilters)}>
          <SlidersHorizontal className="w-4 h-4" /> Filters
        </Button>
      </div>

      {showFilters && (
        <Card className="rounded-xl border border-border/80 bg-white">
          <CardContent className="p-4 flex items-center gap-4 flex-wrap">
            <div className="min-w-[180px]">
              <p className="text-xs font-medium mb-1.5" style={{ color: 'var(--ink-muted)' }}>Status</p>
              <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v === 'all' ? '' : v); setTimeout(fetchPOs, 0); }}>
                <SelectTrigger className="h-9 rounded-lg"><SelectValue placeholder="All" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  {Object.entries(STATUS_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {hasFilters && (
              <Button variant="ghost" size="sm" className="gap-1 text-xs mt-4"
                onClick={() => { setSearch(''); setStatusFilter(''); setTimeout(fetchPOs, 0); }}>
                <X className="w-3.5 h-3.5" /> Clear
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* PO List */}
      <Card className="rounded-xl border border-border/80 bg-white shadow-[0_1px_0_rgba(16,24,40,0.04),0_10px_24px_rgba(16,24,40,0.06)]">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="po-table">
            <thead>
              <tr className="border-b" style={{ borderColor: 'rgba(27,42,74,0.08)' }}>
                {['PO #', 'Vendor', 'Items', 'Total', 'Status', 'Date'].map(h => (
                  <th key={h} className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--ink-muted)' }}>{h}</th>
                ))}
                <th className="w-10" />
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 3 }).map((_, i) => (
                  <tr key={i} className="border-b" style={{ borderColor: 'rgba(27,42,74,0.04)' }}>
                    {Array.from({ length: 7 }).map((_, j) => <td key={j} className="px-4 py-3"><Skeleton className="h-5 w-20" /></td>)}
                  </tr>
                ))
              ) : pos.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-16">
                    <FileText className="w-10 h-10 mx-auto mb-3 opacity-20" style={{ color: 'var(--brand-navy)' }} />
                    <p className="text-sm font-medium" style={{ color: 'var(--navy-ink)' }}>No purchase orders yet</p>
                    <p className="text-xs mt-1" style={{ color: 'var(--ink-muted)' }}>POs are generated from confirmed orders</p>
                  </td>
                </tr>
              ) : (
                pos.map(po => (
                  <tr key={po.id} className="border-b hover:bg-[color:var(--surface-2)] group cursor-pointer"
                    style={{ borderColor: 'rgba(27,42,74,0.04)', transition: 'background-color 120ms' }}>
                    <td className="px-4 py-3">
                      <Link to={`/app/purchase-orders/${po.id}`} className="text-sm font-semibold" style={{ color: 'var(--brand-navy)' }}
                        data-testid={`po-link-${po.id}`}>{po.po_number}</Link>
                    </td>
                    <td className="px-4 py-3 text-sm" style={{ color: 'var(--navy-ink)' }}>{po.vendor_name}</td>
                    <td className="px-4 py-3 text-sm" style={{ color: 'var(--navy-ink)' }}>
                      {po.line_count} &middot; {po.total_metres}m
                    </td>
                    <td className="px-4 py-3 text-sm font-semibold tabular-nums" style={{ color: 'var(--navy-ink)' }}>
                      &#8377;{po.total_amount?.toLocaleString('en-IN')}
                    </td>
                    <td className="px-4 py-3"><StatusBadge status={po.status} /></td>
                    <td className="px-4 py-3 text-sm" style={{ color: 'var(--ink-muted)' }}>{po.po_date}</td>
                    <td className="px-4 py-1">
                      <Link to={`/app/purchase-orders/${po.id}`}>
                        <ChevronRight className="w-4 h-4 opacity-0 group-hover:opacity-50" />
                      </Link>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      </>}
    </div>
  );
}
